import { Theme } from '@mui/material/styles';

/**
 * Generates CSS custom properties from Material-UI theme for Tailwind CSS integration
 * This enables runtime theme switching while using Tailwind utility classes
 */
export const generateTailwindCSSVariables = (theme: Theme): string => {
  return `
    :root {
      /* Primary colors */
      --color-primary-main: ${theme.palette.primary.main};
      --color-primary-contrast: ${theme.palette.primary.contrastText};
      --color-primary-disabled: ${
        theme.palette.primary.contrastTextDisabled || theme.palette.text?.disabled || '#959a9c'
      };
      --color-primary-error: ${theme.palette.primary.contrastError || theme.palette.error.main};
      --color-primary-highlight: ${theme.palette.primary.contrastHighlight || theme.palette.highlight.main};
      
      /* Secondary colors */
      --color-secondary-main: ${theme.palette.secondary.main};
      --color-secondary-contrast: ${theme.palette.secondary.contrastText};
      --color-secondary-disabled: ${
        theme.palette.secondary.contrastTextDisabled || theme.palette.text?.disabled || '#959a9c'
      };
      --color-secondary-highlight: ${theme.palette.secondary.contrastHighlight || theme.palette.highlight.main};
      
      /* Tertiary colors */
      --color-tertiary-main: ${theme.palette.tertiary.main};
      --color-tertiary-contrast: ${theme.palette.tertiary.contrastText};
      --color-tertiary-disabled: ${
        theme.palette.tertiary.contrastTextDisabled || theme.palette.text?.disabled || '#2B3639'
      };
      --color-tertiary-highlight: ${theme.palette.tertiary.contrastHighlight || theme.palette.highlight.main};
      
      /* Quaternary colors */
      --color-quaternary-main: ${theme.palette.quaternary.main};
      --color-quaternary-contrast: ${theme.palette.quaternary.contrastText || '#151a1c'};
      --color-quaternary-dark: ${theme.palette.quaternary.dark || theme.palette.quaternary.main};
      --color-quaternary-highlight: ${theme.palette.quaternary.contrastHighlight || theme.palette.highlight.main};
      
      /* Quinary colors */
      --color-quinary-main: ${theme.palette.quinary.main};
      --color-quinary-contrast: ${theme.palette.quinary.contrastText || '#151a1c'};
      --color-quinary-disabled: ${theme.palette.quinary.contrastTextDisabled || '#2B3639'};
      
      /* Highlight colors */
      --color-highlight-main: ${theme.palette.highlight.main};
      --color-highlight-dark: ${theme.palette.highlight.dark || theme.palette.highlight.main};
      --color-highlight-light: ${theme.palette.highlight.light || theme.palette.highlight.main};
      --color-highlight-contrast: ${theme.palette.highlight.contrastText};
      --color-highlight-contrast-disabled: ${
        theme.palette.highlight.contrastTextDisabled || theme.palette.text?.disabled || '#20282a'
      };
      
      /* Status colors */
      --color-error-main: ${theme.palette.error.main};
      --color-error-dark: ${theme.palette.error.dark || theme.palette.error.main};
      --color-error-light: ${theme.palette.error.light || theme.palette.error.main};
      --color-error-contrast: ${theme.palette.error.contrastText || '#fff'};
      
      --color-warning-main: ${theme.palette.warning.main};
      --color-warning-dark: ${theme.palette.warning.dark || theme.palette.warning.main};
      --color-warning-light: ${theme.palette.warning.light || theme.palette.warning.main};
      --color-warning-contrast: ${theme.palette.warning.contrastText || '#151a1c'};
      
      --color-success-main: ${theme.palette.success.main};
      --color-success-dark: ${theme.palette.success.dark || theme.palette.success.main};
      --color-success-light: ${theme.palette.success.light || theme.palette.success.main};
      --color-success-contrast: ${theme.palette.success.contrastText || '#fff'};
      
      /* Background colors */
      --color-bg-paper: ${theme.palette.background.paper};
      --color-bg-default: ${theme.palette.background.default};
      --color-bg-light: ${theme.palette.background.light || theme.palette.tertiary.main};
      
      /* Text colors */
      --color-text-primary: ${theme.palette.text.primary};
      --color-text-secondary: ${theme.palette.text.secondary};
      --color-text-disabled: ${theme.palette.text.disabled};
      
      /* Common colors */
      --color-common-black: ${theme.palette.common.black};
      --color-common-white: ${theme.palette.common.white};
      --color-common-blue: ${theme.palette.common.blue || '#009ABC'};
      --color-common-red: ${theme.palette.common.red || '#981e32'};
      --color-common-green: ${theme.palette.common.green || '#4caa6f'};

      /* Adaptive overlays: readable on both light and dark surfaces */
      --color-contrast-overlay: color-mix(in srgb, var(--color-text-primary) 10%, transparent);
      --color-contrast-border: color-mix(in srgb, var(--color-text-primary) 20%, transparent);
    }
  `;
};

/**
 * Hook to inject Tailwind CSS variables into the document head
 * This should be called in your main App component
 */
export const useTailwindThemeVariables = (theme: Theme) => {
  if (typeof document !== 'undefined') {
    // Remove existing Tailwind theme styles
    const existingStyle = document.getElementById('tailwind-theme-variables');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new theme styles
    const styleElement = document.createElement('style');
    styleElement.id = 'tailwind-theme-variables';
    styleElement.textContent = generateTailwindCSSVariables(theme);
    document.head.appendChild(styleElement);
  }
};

/**
 * Returns a CSS string that can be used in styled-components or emotion
 */
export const getTailwindThemeCSS = (theme: Theme): string => {
  return generateTailwindCSSVariables(theme);
};
